import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageWrapper from '@/components/PageWrapper';
import ImageCarousel from '@/components/ImageCarousel';
import { FadeUp, SlideLeft, SlideRight, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const HomePage = () => {
  // Real images
  const galleryImages = [
    { src: '/images/TEASER HERO.jpg', alt: 'Teaser pose', caption: 'The Teaser' },
    { src: '/images/REFORMER HERO.jpg', alt: 'Reformer', caption: 'Reformer Work' },
    { src: '/images/LADDER BARREL HERO 1.jpg', alt: 'Ladder Barrel', caption: 'Ladder Barrel' },
    { src: '/images/CHAIR HERO 2.jpg', alt: 'Chair', caption: 'Chair Work' },
    { src: '/images/HERO CADI CLOSER SHOT.jpg', alt: 'Cadillac', caption: 'Cadillac' },
    { src: '/images/THE STUDIO.jpg', alt: 'The Studio', caption: 'The Studio' },
  ];

  const services = [
    {
      title: '1:1 Sessions',
      description: 'Each session is fully customised and taught one-on-one in a private studio environment.',
      image: '/images/REFORMER SNAKE.jpg',
      link: '/services/sessions',
    },
    {
      title: 'The Studio',
      description: 'A quiet, private studio designed for focused, distraction-free movement.',
      image: '/images/The Studio Wide.jpg',
      link: '/services/studio',
    },
    {
      title: 'Equipment',
      description: 'Sessions may include work on: Reformer, Cadillac, Chair, Classical Ladder Barrel.',
      image: '/images/REFEORMER.jpg',
      link: '/services/equipment',
    },
    {
      title: 'Comprehensive Approach',
      description: 'Comprehensive training means a deep understanding of how the body moves.',
      image: '/images/LADDER BARREL TREE HERO.jpg',
      link: '/services/comprehensive',
    },
  ];

  const focusAreas = [
    { title: 'Posture', description: 'Build alignment you can maintain beyond the studio.' },
    { title: 'Mobility', description: 'Create controlled range of motion that supports joints and movement quality.' },
    { title: 'Strength', description: 'Develop strength that integrates into everyday life, sport, and long-term health.' },
  ];

  return (
    <PageWrapper>
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
                  Private Pilates · Kingsland, Auckland
                </p>
                <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-stone-900 leading-[1.1] tracking-tight">
                  Unlock Your<br />
                  <span className="italic">Best Self</span>
                </h1>
                <p className="mt-8 text-stone-600 text-lg leading-relaxed max-w-lg font-light">
                  This is Pilates taught with intention. Every session is private and fully tailored — 
                  designed to help you move better, feel stronger, and understand your body more clearly.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Button
                    asChild
                    data-testid="hero-book-btn"
                    className="btn-primary"
                  >
                    <Link to="/bookings">
                      Book a Session
                      <ArrowRight className="ml-2" size={16} strokeWidth={1.5} />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    data-testid="hero-learn-btn"
                    className="btn-secondary"
                  >
                    <Link to="/about">Learn More</Link>
                  </Button>
                </div>
              </motion.div>
            </div>

            {/* Hero Image */}
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              <div className="image-zoom aspect-[4/5] lg:aspect-[3/4]">
                <img
                  src="/images/TEASER HERO.jpg"
                  alt="Otto Prodan - Teaser"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tagline Section */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.1] tracking-tight">
                Classical. Contemporary.<br />
                <span className="italic">Complete.</span>
              </h2>
              <p className="mt-8 text-stone-600 text-lg md:text-xl leading-relaxed font-light">
                Private one-on-one Pilates focused on refining technique to improve posture, 
                mobility, and strength. Classical foundations, applied intelligently for the modern body.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Image Gallery Carousel */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <ImageCarousel images={galleryImages} />
          </FadeUp>
        </div>
      </section>

      {/* Expert-Led Guidance */}
      <section data-testid="expert-section" className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">
                Expert Instruction
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl text-white leading-[1.1] tracking-tight">
                Expert-Led<br />
                <span className="italic">Guidance</span>
              </h2>
              <p className="mt-8 text-stone-300 text-lg leading-relaxed font-light">
                Whether you're new to Pilates or highly experienced, the work meets you 
                where you are and evolves with you. Sessions are designed to help you 
                move better, feel stronger, and understand your body more clearly.
              </p>
              <Button
                asChild
                data-testid="expert-about-btn"
                className="mt-8 bg-white text-stone-900 hover:bg-stone-100 rounded-none px-8 py-3 uppercase tracking-widest text-xs font-medium"
              >
                <Link to="/about">About Otto</Link>
              </Button>
            </SlideLeft>

            <SlideRight>
              <div className="grid grid-cols-2 gap-4">
                <div className="image-zoom aspect-[3/4]">
                  <img
                    src="/images/CHAIR PIKE.jpg"
                    alt="Chair Pike"
                    className="w-full h-full object-cover editorial-image"
                  />
                </div>
                <div className="image-zoom aspect-[3/4] mt-8">
                  <img
                    src="/images/LADDER BARREL BACKBEND.jpg"
                    alt="Ladder Barrel"
                    className="w-full h-full object-cover editorial-image"
                  />
                </div>
              </div>
            </SlideRight>
          </div>
        </div>
      </section>

      {/* The Focus */}
      <section data-testid="focus-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 text-center">
              Areas of Focus
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight text-center mb-16">
              The <span className="italic">Focus</span>
            </h2>
          </FadeUp>

          <StaggerContainer className="grid md:grid-cols-3 gap-8">
            {focusAreas.map((area) => (
              <StaggerItem key={area.title}>
                <div
                  data-testid={`focus-card-${area.title.toLowerCase()}`}
                  className="focus-card border border-stone-200 bg-white"
                >
                  <h3 className="font-heading text-2xl text-stone-900 mb-4">{area.title}</h3>
                  <p className="text-stone-600 leading-relaxed font-light">{area.description}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
              What We Offer
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight mb-16">
              Services & <span className="italic">Studio</span>
            </h2>
          </FadeUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <StaggerItem key={service.title}>
                <Link
                  to={service.link}
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group block"
                >
                  <div className="service-card h-full">
                    <div className="image-zoom aspect-[16/10] mb-6">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover editorial-image"
                      />
                    </div>
                    <h3 className="font-heading text-2xl text-stone-900 mb-3 group-hover:text-stone-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-stone-600 leading-relaxed font-light">{service.description}</p>
                    <span className="inline-flex items-center mt-4 text-xs uppercase tracking-widest text-stone-900 group-hover:gap-3 transition-all">
                      Learn More
                      <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Bottom Image Row */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <div className="grid grid-cols-3 gap-4">
              <div className="image-zoom aspect-[4/5]">
                <img
                  src="/images/REFORMER TENDON.jpg"
                  alt="Reformer Tendon"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
              <div className="image-zoom aspect-[4/5]">
                <img
                  src="/images/otto.jpeg"
                  alt="Otto Prodan"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
              <div className="image-zoom aspect-[4/5]">
                <img
                  src="/images/CAD.jpg"
                  alt="Cadillac"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
                Experience TEZR
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight">
                Ready to Begin Your <span className="italic">Journey</span>?
              </h2>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                Experience the difference of private, one-on-one Pilates instruction.
              </p>
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <Button
                  asChild
                  data-testid="cta-book-btn"
                  className="btn-primary"
                >
                  <Link to="/bookings">
                    Book Your Session
                    <ArrowRight className="ml-2" size={16} strokeWidth={1.5} />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="cta-contact-btn"
                  className="btn-secondary"
                >
                  <Link to="/contact">Get in Touch</Link>
                </Button>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
};

export default HomePage;
